import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Radio, Spin, Empty } from 'antd';
import { DollarOutlined, PercentageOutlined, ShoppingCartOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import apiClient from '../services/api';
import { getQuoteStatusLabel } from '../utils/quoteUtils';
import { formatCurrency, formatNumber } from '../utils/format';

interface ReportsData {
  total_revenue: number;
  conversion_rate: number;
  avg_order_value: number;
  avg_response_time: number;
  revenue_by_period: Array<{ period: string; revenue: number }>;
  quotes_by_status: Record<string, number>;
  top_service_providers: Array<{ name: string; revenue: number }>;
  top_products: Array<{ name: string; revenue: number }>;
}

const PIE_COLORS = ['#1890ff', '#fa8c16', '#52c41a', '#f5222d', '#fa541c'];

const Reports: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<ReportsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<string>('monthly');

  useEffect(() => {
    loadReports();
  }, [period]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/reports/summary', {
        params: { period },
      });
      setData(response.data);
    } catch (error: any) {
      setData({
        total_revenue: 0,
        conversion_rate: 0,
        avg_order_value: 0,
        avg_response_time: 0,
        revenue_by_period: [],
        quotes_by_status: {},
        top_service_providers: [],
        top_products: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const pieData = Object.entries(data?.quotes_by_status || {})
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({
      name: getQuoteStatusLabel(status, t),
      value: count,
    }));

  const hasTopServiceProviders = (data?.top_service_providers || []).length > 0;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0 }}>
          {t('reports.title')}
        </h1>
        <Radio.Group
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="daily">{t('reports.daily')}</Radio.Button>
          <Radio.Button value="weekly">{t('reports.weekly')}</Radio.Button>
          <Radio.Button value="monthly">{t('reports.monthly')}</Radio.Button>
        </Radio.Group>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('reports.revenue')}
              value={data?.total_revenue || 0}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(Number(value), 'EUR', i18n.language)}
              valueStyle={{ color: '#BD7423' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('reports.conversionRate')}
              value={data?.conversion_rate || 0}
              prefix={<PercentageOutlined />}
              formatter={(value) => `${formatNumber(Number(value), i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('reports.avgOrderValue')}
              value={data?.avg_order_value || 0}
              prefix={<ShoppingCartOutlined />}
              formatter={(value) => formatCurrency(Number(value), 'EUR', i18n.language)}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('reports.avgResponseTime')}
              value={data?.avg_response_time || 0}
              prefix={<ClockCircleOutlined />}
              formatter={(value) => `${formatNumber(Number(value), i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={t('reports.revenueByPeriod')} style={{ marginBottom: 16 }}>
            {(data?.revenue_by_period || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data?.revenue_by_period || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => formatCurrency(Number(value), 'EUR', i18n.language)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#BD7423"
                    strokeWidth={2}
                    dot={{ fill: '#334E3B' }}
                    name={t('reports.revenue')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('dashboard.noData')} />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t('reports.quotesByStatus')} style={{ marginBottom: 16 }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('dashboard.noData')} />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {hasTopServiceProviders && (
          <Col xs={24} lg={12}>
            <Card title={t('reports.topSPs')}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data?.top_service_providers || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value: any) => formatCurrency(Number(value), 'EUR', i18n.language)} />
                  <Bar dataKey="revenue" fill="#334E3B" name={t('reports.revenue')} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        )}

        <Col xs={24} lg={hasTopServiceProviders ? 12 : 24}>
          <Card title={t('reports.topProducts')}>
            {(data?.top_products || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data?.top_products || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value: any) => formatCurrency(Number(value), 'EUR', i18n.language)} />
                  <Bar dataKey="revenue" fill="#BD7423" name={t('reports.revenue')} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('dashboard.noData')} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Reports;
